<template>
  <div class="answer-type-selector">
    <section class="answer-panel">
      <h5 class="answer-panel__title"> {{$t('general.answer_type')}} </h5>
      <p class="answer-panel__help"> {{$t('general.how_user_answer_task')}} </p>

      <div class="answer-choices" role="radiogroup" :aria-label="$t('general.answer_type')">
        <button
          type="button"
          class="answer-choice"
          :class="{ 'answer-choice--active': isAnswerTypeMission === 'file' }"
          :aria-pressed="isAnswerTypeMission === 'file'"
          @click="onAnswerTypeChange('file')"
        >
          <i class="uil-upload" aria-hidden="true"></i>
          <span> {{$t('general.let_him_upload_file')}}</span>
        </button>

        <button
          type="button"
          class="answer-choice"
          :class="{ 'answer-choice--active': isAnswerTypeMission === 'text' }"
          :aria-pressed="isAnswerTypeMission === 'text'"
          @click="onAnswerTypeChange('text')"
        >
          <i class="uil-text" aria-hidden="true"></i>
          <span> {{$t('general.let_him_write_answer')}}</span>
        </button>
      </div>

      <label
        class="admin-row"
        :class="{ 'is-on': isAdminApproved }"
        for="complete_type_3"
      >
        <input
          id="complete_type_3"
          class="admin-row__input"
          type="checkbox"
          :checked="isAdminApproved"
          @click="toggleAdminApproval"
        />
        <span class="admin-row__switch" aria-hidden="true">
          <span class="admin-row__thumb"></span>
        </span>
        <span class="admin-row__text">
          <span class="admin-row__title"> {{$t('general.admin_approval_required')}}</span>
          <span class="admin-row__desc"> {{$t('general.task_completed_once_admin_approves')}}</span>
        </span>
      </label>

      <div class="result-box" :class="{ 'result-box--flash': resultFlash }">
        <span class="result-box__label"> {{$t('general.completion_rule')}}</span>
        <strong class="result-box__value">{{ completionSummary }}</strong>
      </div>

      <div
        v-if="completion_rule === 'correct_answer_given'"
        class="correct-answer"
      >
        <label class="correct-answer__label" for="correct_answer">{{$t('general.correct_answer')}}</label>
        <input
          id="correct_answer"
          name="correct_answer"
          type="text"
          class="correct-answer__input"
          :value="accepted_right_answer"
          :placeholder="$t('general.write_expected_answer')"
          @keyup="setAnswer"
        />
      </div>
    </section>
  </div>
</template>

<script>
export default {
  props: {
    isAnswerTypeMission: String,
    completion_rule: {
      type: String,
      default: "answered",
    },
    accepted_right_answer: String,
  },
  data() {
    return {
      resultFlash: false,
      resultFlashTimer: null,
    };
  },
  computed: {
    isAdminApproved() {
      return this.completion_rule === "admin_approved";
    },
    completionSummary() {
      if (this.isAdminApproved) return this.$t('general.task_completed_once_admin_approved') ;
      if (this.completion_rule === "correct_answer_given") return this.$t('general.task_completed_when_correct_answer_given');
      return this.$t('general.task_completed_when_file_uploaded') ;
    },
  },
  watch: {
    completion_rule() {
      this.flashResult();
    },
  },
  beforeDestroy() {
    if (this.resultFlashTimer) clearTimeout(this.resultFlashTimer);
  },
  methods: {
    flashResult() {
      this.resultFlash = true;
      if (this.resultFlashTimer) clearTimeout(this.resultFlashTimer);
      this.resultFlashTimer = setTimeout(() => {
        this.resultFlash = false;
      }, 500);
    },
    toggleAdminApproval() {
      if (this.completion_rule === "admin_approved") {
        if (this.isAnswerTypeMission === "text") {
          this.$emit("update:completion_rule", "correct_answer_given");
        } else {
          this.$emit("update:completion_rule", "answered");
        }
      } else {
        this.$emit("update:completion_rule", "admin_approved");
        this.$emit("update:accepted_right_answer", null);
      }
    },
    onAnswerTypeChange(value) {
      this.$emit("update:answerType", value);

      if (this.completion_rule === "admin_approved") {
        this.$emit("update:accepted_right_answer", null);
        return;
      }
      if (value === "file") {
        this.$emit("update:accepted_right_answer", null);
        this.$emit("update:completion_rule", "answered");
      } else if (value === "text") {
        this.$emit("update:completion_rule", "correct_answer_given");
      }
    },
    setAnswer(e) {
      this.$emit("update:accepted_right_answer", e.target.value);
    },
  },
};
</script>

<style scoped>
.answer-type-selector {
  width: 100%;
}

.answer-panel {
  padding: 16px;
  background: #fff;
  border: 1px solid rgba(62, 65, 109, 0.14);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(62, 65, 109, 0.05);
}

.answer-panel__title {
  margin: 0;
  color: #3e416d;
  font-size: 16px;
  font-weight: 800;
}

.answer-panel__help {
  margin: 4px 0 14px;
  color: #6c6f8c;
  font-size: 13px;
  font-weight: 500;
}

.answer-choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
}

.answer-choice {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 14px;
  border: 2px solid rgba(62, 65, 109, 0.16);
  border-radius: 12px;
  background: #f8f9fb;
  color: #6c6f8c;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.answer-choice i {
  font-size: 18px;
}

.answer-choice:hover {
  border-color: rgba(62, 65, 109, 0.35);
  color: #3e416d;
  background: #fff;
}

.answer-choice--active {
  border-color: #3e416d;
  background: #3e416d;
  color: #fff;
  box-shadow: 0 6px 14px rgba(62, 65, 109, 0.22);
}

.admin-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin: 0 0 12px;
  border: 1px solid rgba(62, 65, 109, 0.12);
  border-radius: 12px;
  background: #f8f9fb;
  cursor: pointer;
}

.admin-row__input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.admin-row__switch {
  position: relative;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  background: #d7dae6;
  flex-shrink: 0;
  transition: background-color 0.2s ease;
}

.admin-row__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
  transition: transform 0.2s ease;
}

.admin-row__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.admin-row__title {
  color: #3e416d;
  font-size: 13px;
  font-weight: 700;
}

.admin-row__desc {
  color: #6c6f8c;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
}

.admin-row.is-on {
  border-color: rgba(62, 65, 109, 0.28);
  background: rgba(62, 65, 109, 0.06);
}

.admin-row.is-on .admin-row__switch {
  background: #3e416d;
}

.admin-row.is-on .admin-row__thumb {
  transform: translateX(18px);
}

.result-box {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(62, 65, 109, 0.08);
  border: 1px solid rgba(62, 65, 109, 0.16);
}

.result-box--flash {
  animation: result-flash 0.45s ease;
}

.result-box__label {
  color: #6c6f8c;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.result-box__value {
  color: #3e416d;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.3;
}

.correct-answer {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
}

.correct-answer__label {
  margin: 0;
  color: #3e416d;
  font-size: 13px;
  font-weight: 700;
}

.correct-answer__input {
  width: 100%;
  height: 42px;
  padding: 0 12px;
  border: 1px solid #d8dbe7;
  border-radius: 10px;
  background: #f8f9fb;
  color: #3e416d;
  font-size: 14px;
  font-weight: 600;
}

.correct-answer__input:focus {
  outline: none;
  border-color: #3e416d;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.12);
}

@keyframes result-flash {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(62, 65, 109, 0.16);
  }
  40% {
    transform: scale(1.01);
    box-shadow: 0 0 0 4px rgba(62, 65, 109, 0.14);
  }
  100% {
    transform: scale(1);
    box-shadow: none;
  }
}

@media (max-width: 767px) {
  .answer-choices {
    grid-template-columns: 1fr;
  }
}
</style>
